import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../../store';
import { urlParamReplicaId, urlProjectDocumentsFiles } from '../../../Constants';
import { apiFetch } from '../../../util/ProjectUtils';

const initialState: IProjectDocumentsFilesState = {
    data: null,
    loading: false,
    error: null,
};

const reducers = {
    resetState(state: IProjectDocumentsFilesState) {
        Object.assign(state, initialState);
    },
};

const thunks = {
    getProjectDocumentsFiles: async (config: {
        replicaId: string;
        universalIds: string[];
    }): Promise<Record<string, IProjectDocumentFileInfo[]>> => {
        try {
            const { universalIds, replicaId } = config;
            const request: RequestInit = {
                headers: {
                    universalIds: universalIds.join('|'),
                },
            };

            const url = urlProjectDocumentsFiles.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsFilesState>) => {
        builder.addCase(
            getProjectDocumentsFiles.pending,
            (state: Draft<IProjectDocumentsFilesState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectDocumentsFiles.fulfilled,
            (
                state: Draft<IProjectDocumentsFilesState>,
                action: IAction<Record<string, IProjectDocumentFileInfo[]>>,
            ) => {
                state.loading = false;
                state.data = action.payload;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectDocumentsFiles.rejected,
            (state: Draft<IProjectDocumentsFilesState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const getProjectDocumentsFiles = createAsyncThunk(
    urlProjectDocumentsFiles,
    thunks.getProjectDocumentsFiles,
);

interface IProjectDocumentFileInfo {
    fileName: string;
    fileSize: number;
    fileIndex: number;
}

export type IProjectDocumentsFilesState = SlicePropsWithData<
    Record<string, IProjectDocumentFileInfo[]>
>;

export const projectDocumentsFiles = createSlice({
    name: 'projectDocumentsFiles',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const { resetState } = projectDocumentsFiles.actions;

export default projectDocumentsFiles.reducer;
